/**
 * AlertBanner — token-aligned inline notification surface with optional
 * dismiss.
 *
 * Sits above a {@link Toast} (transient, viewport-anchored) and below a
 * {@link DecisionReceipt} (rich, witness-shaped). Use it for inline
 * "this demo is read-only", "you are out of seats", or
 * "neutralization succeeded — see the receipt" messages.
 *
 * Single responsibility: render a single-line accent-stripe banner with
 * an icon, title, optional description, optional action slot, and an
 * optional dismiss button. The component is uncontrolled by default;
 * pass `onDismiss` to take ownership of the visibility lifecycle.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { AlertTriangle, CheckCircle2, Info, MinusCircle, X, type LucideIcon } from "lucide-react";
import React, { useState } from "react";
import { GRAY, PRIMARY, SAFE, WARN } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

export type AlertBannerTone = "info" | "success" | "warning" | "danger" | "neutral";

export interface AlertBannerProps {
  /** Visual tone — picks an accent stripe and icon. */
  readonly tone?: AlertBannerTone;
  /** Short title rendered in bold. */
  readonly title: React.ReactNode;
  /** Optional supporting description. */
  readonly description?: React.ReactNode;
  /** Optional override for the leading glyph. */
  readonly icon?: React.ReactNode;
  /** Optional action slot — e.g. a "Retry" button. */
  readonly action?: React.ReactNode;
  /**
   * Dismiss handler. When omitted the banner has no close button and
   * is treated as permanent. When provided, the banner manages its
   * own visibility and calls back once.
   */
  readonly onDismiss?: () => void;
  /** Accessible role override. Defaults to `alert` for `warning`/`danger`, otherwise `status`. */
  readonly role?: "status" | "alert";
  /** Additional className appended to the wrapper. */
  readonly className?: string;
}

interface TonePalette {
  readonly accent: string;
  readonly background: string;
  readonly foreground: string;
  readonly Icon: LucideIcon;
}

const TONE_PALETTE: Readonly<Record<AlertBannerTone, TonePalette>> = {
  info: { accent: PRIMARY[600], background: PRIMARY[100], foreground: PRIMARY[800], Icon: Info },
  success: {
    accent: SAFE[500],
    background: SAFE[100],
    foreground: SAFE[700],
    Icon: CheckCircle2,
  },
  warning: {
    accent: WARN[500],
    background: WARN[100],
    foreground: WARN[700],
    Icon: AlertTriangle,
  },
  danger: { accent: WARN[700], background: WARN[300], foreground: WARN[700], Icon: X },
  neutral: {
    accent: GRAY[600],
    background: GRAY[100],
    foreground: GRAY[900],
    Icon: MinusCircle,
  },
};

function defaultRole(tone: AlertBannerTone): "status" | "alert" {
  return tone === "warning" || tone === "danger" ? "alert" : "status";
}

/** Renders an accent-striped banner with optional dismiss. */
export const AlertBanner = React.memo(function AlertBanner({
  tone = "info",
  title,
  description,
  icon,
  action,
  onDismiss,
  role,
  className,
}: AlertBannerProps): React.JSX.Element | null {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const palette = TONE_PALETTE[tone];
  const resolvedRole = role ?? defaultRole(tone);
  const DefaultIcon = palette.Icon;

  return (
    <div
      role={resolvedRole}
      aria-live={resolvedRole === "alert" ? "assertive" : "polite"}
      data-csk-alert-tone={tone}
      className={cn("font-body", className)}
      style={{
        background: palette.background,
        border: `1px solid ${palette.accent}`,
        borderLeft: `4px solid ${palette.accent}`,
        borderRadius: "0.5rem",
        color: palette.foreground,
        display: "flex",
        gap: "0.75rem",
        padding: "0.75rem 0.9rem",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          alignSelf: "flex-start",
          color: palette.accent,
          fontSize: "1rem",
          lineHeight: 1.2,
        }}
      >
        {icon ?? <DefaultIcon aria-hidden="true" size={18} />}
      </span>
      <div style={{ flex: 1, fontSize: "0.875rem", lineHeight: 1.45 }}>
        <div style={{ color: palette.foreground, fontWeight: 600 }}>{title}</div>
        {description !== undefined && (
          <div style={{ color: GRAY[700], marginTop: "0.2rem" }}>{description}</div>
        )}
        {action !== undefined && <div style={{ marginTop: "0.5rem" }}>{action}</div>}
      </div>
      {onDismiss !== undefined && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => {
            setDismissed(true);
            onDismiss();
          }}
          style={{
            alignSelf: "flex-start",
            background: "none",
            border: 0,
            color: GRAY[600],
            cursor: "pointer",
            fontSize: "0.9rem",
            padding: 0,
          }}
          data-csk-alert-dismiss
        >
          <X aria-hidden="true" size={16} />
        </button>
      )}
    </div>
  );
});
